import React, { ReactNode } from "react";
import { StyleSheet, css } from "aphrodite";
import Flex from "../core/Flex";
import { screenerStyles } from "./careScreenerStyles";
import { insuranceStyles } from "./insurance/insuranceStyles";
import Button from "../Button";
import { EligibilityEstimate } from "../../services/CareScreenerService";

type Props = {
  childName?: string;
  estimate: EligibilityEstimate | undefined;
  onContinue: () => void;
  progressComponent?: ReactNode;
};

export default function CoverageResult({ childName, estimate, onContinue, progressComponent }: Props) {
  const memberObligationInCents = estimate?.memberObligationInCents;
  const showCost = memberObligationInCents != undefined;

  return (
    <Flex className={css(screenerStyles.whiteContainerRoundedEdges, screenerStyles.padding, screenerStyles.container)}>
      {progressComponent}
      <Flex flexGrow center>
        <p className={css(insuranceStyles.title)}>You're covered! 🎉</p>
        {showCost && (
          <>
            <p className={css(insuranceStyles.bodyText)}>
              We've checked your insurance plan. Your <span className={css(insuranceStyles.bold)}>estimated cost</span>{" "}
              is:
            </p>
            <div className={css(styles.costRow)}>
              <span className={css(insuranceStyles.sessionCostText)}>55-min sessions</span>
              <div className={css(styles.divider)} />
              <span className={css(insuranceStyles.sessionPrice)}>
                ${convertCentsToDollars(memberObligationInCents)}*
              </span>
            </div>
            {renderBenefitProgression(estimate, memberObligationInCents)}
            <p className={css(insuranceStyles.bodyText, styles.disclaimer)}>
              *Insurance rates are subject to change
            </p>
          </>
        )}
      </Flex>
      <Flex center bottom>
        <Button
          title={childName ? `book ${childName}'s intake` : "Book your session"}
          theme="manaBlue"
          fullWidth
          onPress={onContinue}
        />
      </Flex>
    </Flex>
  );
}

function renderBenefitProgression(
  estimate: EligibilityEstimate | undefined,
  memberObligationInCents: number
): ReactNode {
  if (!estimate) return null;

  const showDeductible =
    estimate.coinsurancePercent != undefined &&
    estimate.coinsurancePercent > 0 &&
    estimate.deductibleInCents != undefined &&
    estimate.deductibleInCents > 0;

  const showOopMax = estimate.outOfPocketInCents != undefined && estimate.outOfPocketInCents > 0;

  if (!showDeductible && !showOopMax) return null;

  return (
    <>
      {showDeductible &&
        renderThresholdProgression({
          thresholdAmountInCents: estimate.deductibleInCents,
          amountInCentsUntilThresholdMet: estimate.remainingDeductibleInCents,
          obligationBeforeThresholdMet: memberObligationInCents,
          obligationAfterThresholdMet: estimate.postDeductibleMemberObligationInCents,
          benefitDescription: "deductible"
        })}
      {showOopMax &&
        renderThresholdProgression({
          thresholdAmountInCents: estimate.outOfPocketInCents,
          amountInCentsUntilThresholdMet: estimate.remainingOutOfPocketInCents,
          obligationBeforeThresholdMet: memberObligationInCents,
          obligationAfterThresholdMet: estimate.postOopMaxMemberObligationInCents,
          benefitDescription: "out-of-pocket max"
        })}
      <p className={css(insuranceStyles.bodyText)}>The hard part's over - just a couple more steps.</p>
    </>
  );
}

function renderThresholdProgression({
  thresholdAmountInCents,
  amountInCentsUntilThresholdMet,
  obligationBeforeThresholdMet,
  obligationAfterThresholdMet,
  benefitDescription
}: {
  thresholdAmountInCents: number | undefined;
  amountInCentsUntilThresholdMet: number | undefined;
  obligationBeforeThresholdMet: number;
  obligationAfterThresholdMet: number | undefined;
  benefitDescription: "deductible" | "out-of-pocket max";
}): ReactNode {
  if (amountInCentsUntilThresholdMet == undefined) {
    return (
      <p className={css(insuranceStyles.bodyText)}>
        We'll need to check your {benefitDescription} to see if it has been met.
      </p>
    );
  }

  if (amountInCentsUntilThresholdMet === 0) {
    return (
      <p className={css(insuranceStyles.bodyText)}>
        You've met your {benefitDescription}
        {thresholdAmountInCents && thresholdAmountInCents > 0 ? (
          <> of {renderAmount(thresholdAmountInCents)}.</>
        ) : (
          <>.</>
        )}
      </p>
    );
  }

  if (amountInCentsUntilThresholdMet > 0 && obligationAfterThresholdMet != undefined) {
    return (
      <p className={css(insuranceStyles.bodyText)}>
        Once you've met your {benefitDescription}, your out-of-pocket responsibility will go from{" "}
        {renderAmount(obligationBeforeThresholdMet)} to {renderAmount(obligationAfterThresholdMet)}.
      </p>
    );
  }

  return null;
}

function renderAmount(amountInCents: number): ReactNode {
  return (
    <span className={css(insuranceStyles.darkBlue, insuranceStyles.bold)}>${convertCentsToDollars(amountInCents)}</span>
  );
}

function convertCentsToDollars(numberInCents: number) {
  if (numberInCents % 100 !== 0) {
    return Number(numberInCents / 100).toFixed(2);
  }
  return numberInCents / 100;
}

const styles = StyleSheet.create({
  costRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 20,
    padding: "10px 30px",
    margin: "0 20px"
  },
  divider: {
    width: 1,
    height: 56,
    backgroundColor: "#D0D0D0"
  },
  disclaimer: {
    fontSize: 16,
    lineHeight: 1.3,
    paddingTop: 5
  }
});
